import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRevalidateOnFocus } from './useRevalidateOnFocus';

interface Page<T> {
  data: T[];
  nextCursor: string | null;
}

interface FetchRequest {
  kind: 'reset' | 'append';
  cursor: string | null;
  /** Bumped on every loadMore/reload so the effect re-runs deterministically. */
  nonce: number;
}

export interface UsePaginatedList<T> {
  items: T[];
  nextCursor: string | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  loadMore: () => void;
  reload: () => void;
}

export function usePaginatedList<T>(
  fetcher: (cursor: string | null, signal: AbortSignal) => Promise<Page<T>>,
  deps: ReadonlyArray<unknown>
): UsePaginatedList<T> {
  const [items, setItems] = useState<T[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset signal: changes whenever `deps` change OR reload() is called.
  const [resetNonce, setResetNonce] = useState(0);
  const depsKey = useMemo(() => Symbol(), deps); // eslint-disable-line react-hooks/exhaustive-deps

  // Append signal: changes only when loadMore() is called.
  const [appendRequest, setAppendRequest] = useState<FetchRequest | null>(null);

  const cursorRef = useRef<string | null>(null);
  const resettingRef = useRef(true);
  const appendingRef = useRef(false);
  const itemsRef = useRef<T[]>([]);
  itemsRef.current = items;
  const prevDepsKeyRef = useRef(depsKey);

  const reload = useCallback(() => {
    setResetNonce((n) => n + 1);
  }, []);

  useRevalidateOnFocus(reload);

  const loadMore = useCallback(() => {
    const cursor = cursorRef.current;
    if (cursor === null || resettingRef.current || appendingRef.current) return;
    appendingRef.current = true;
    setAppendRequest({ kind: 'append', cursor, nonce: Date.now() + Math.floor(performance.now()) });
  }, []);

  // Reset effect — fires on deps change and on reload().
  useEffect(() => {
    const ctrl = new AbortController();
    let alive = true;
    const filtersChanged = prevDepsKeyRef.current !== depsKey;
    prevDepsKeyRef.current = depsKey;
    const soft = !filtersChanged && itemsRef.current.length > 0;
    resettingRef.current = true;
    appendingRef.current = false;
    cursorRef.current = null;
    setNextCursor(null);
    setIsLoading(true);
    setIsLoadingMore(false);

    if (!soft) {
      setItems([]);
    }
    setError(null);
    setAppendRequest(null); // cancel any pending append for the previous filters
    fetcher(null, ctrl.signal)
      .then((page) => {
        if (!alive) return;
        setItems(page.data);
        setNextCursor(page.nextCursor);
        cursorRef.current = page.nextCursor;
        resettingRef.current = false;
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Falha ao carregar dados');
        resettingRef.current = false;
        setIsLoading(false);
      });
    return () => {
      alive = false;
      ctrl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depsKey, resetNonce]);

  // Append effect — fires only when loadMore() pushes a new request.
  useEffect(() => {
    if (!appendRequest) return;
    const ctrl = new AbortController();
    let alive = true;
    setIsLoadingMore(true);
    setError(null);
    fetcher(appendRequest.cursor, ctrl.signal)
      .then((page) => {
        if (!alive) return;
        setItems((prev) => [...prev, ...page.data]);
        setNextCursor(page.nextCursor);
        cursorRef.current = page.nextCursor;
        appendingRef.current = false;
        setIsLoadingMore(false);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Falha ao carregar dados');
        appendingRef.current = false;
        setIsLoadingMore(false);
      });
    return () => {
      alive = false;
      ctrl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appendRequest]);

  return { items, nextCursor, isLoading, isLoadingMore, error, loadMore, reload };
}
